import React, { useState, useCallback } from 'react';
import type firebase from 'firebase/compat/app';
import type { Department, Shift, PickupLocation, ReservationSource, DayOfWeek, AdminUser, LoginCredentials } from '../../types';
import { ALL_DAYS } from '../../types';
import { Icons } from '../ui/Icons';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';
import AdminManagementView from '../admin/AdminManagementView';

interface SettingsViewProps {
    // Core Settings
    departments: Department[];
    shifts: Shift[];
    pickupLocations: PickupLocation[];
    reservationSources: ReservationSource[];
    weekStartDay: DayOfWeek;
    propertyName: string;
    onSaveDepartment: (department: Omit<Department, 'id'> & { id?: string }) => void;
    onDeleteDepartment: (id: string) => void;
    onSaveShift: (shift: Omit<Shift, 'id'> & { id?: string }) => void;
    onDeleteShift: (id: string) => void;
    onSavePickupLocation: (location: Omit<PickupLocation, 'id'> & { id?: string }) => void;
    onDeletePickupLocation: (id: string) => void;
    onSaveReservationSource: (source: Omit<ReservationSource, 'id'> & { id?: string }) => void;
    onDeleteReservationSource: (id: string) => void;
    onUpdateWeekStartDay: (day: DayOfWeek) => void;
    onUpdatePropertyName: (name: string) => void;

    // Admin Management (Moved into Settings)
    currentUser: firebase.User;
    adminUsers: AdminUser[];
    onCreateAdmin: (credentials: LoginCredentials) => Promise<void>;
    onDeleteCurrentUser: () => Promise<void>;
    onUpdateAdminStatus: (uid: string, status: 'active' | 'suspended') => Promise<void>;
    onDeleteAdmin: (uid: string, email: string) => Promise<void>;
    onSaveAdmin: (admin: AdminUser) => Promise<void>;
}

type SettingsTab = 'general' | 'departments' | 'shifts' | 'locations' | 'sources' | 'admins';

const SettingsView: React.FC<SettingsViewProps> = ({ 
    departments, shifts, pickupLocations, reservationSources, weekStartDay, propertyName,
    onSaveDepartment, onDeleteDepartment, 
    onSaveShift, onDeleteShift, 
    onSavePickupLocation, onDeletePickupLocation, 
    onSaveReservationSource, onDeleteReservationSource,
    onUpdateWeekStartDay, onUpdatePropertyName,
    currentUser, adminUsers, onCreateAdmin, onDeleteCurrentUser, onUpdateAdminStatus, onDeleteAdmin, onSaveAdmin
}) => {
    const [activeTab, setActiveTab] = useState<SettingsTab>('general');
    
    // Department State
    const [depName, setDepName] = useState('');
    const [depColor, setDepColor] = useState('#3b82f6');
    const [editingDep, setEditingDep] = useState<Department | null>(null);

    // Shift State
    const [shiftName, setShiftName] = useState('');
    const [shiftStartTime, setShiftStartTime] = useState('09:00');
    const [shiftEndTime, setShiftEndTime] = useState('17:00');
    const [shiftColor, setShiftColor] = useState('#34d399');
    const [editingShift, setEditingShift] = useState<Shift | null>(null);

    // Pickup Location State
    const [locationName, setLocationName] = useState('');
    const [locationAddress, setLocationAddress] = useState('');
    const [locationLat, setLocationLat] = useState<number>(0);
    const [locationLng, setLocationLng] = useState<number>(0);
    const [locationColor, setLocationColor] = useState('#f59e0b');
    const [editingLocation, setEditingLocation] = useState<PickupLocation | null>(null);

    // Reservation Source State
    const [sourceName, setSourceName] = useState('');
    const [editingSource, setEditingSource] = useState<ReservationSource | null>(null);

    const departmentDeleteHandler = useDeleteHandler<Department>({
        onDeleteAsync: async (dep) => onDeleteDepartment(dep.id),
        entityName: 'Department',
        getDisplayName: (dep) => dep.name,
        getItemId: (dep) => dep.id,
    });

    const shiftDeleteHandler = useDeleteHandler<Shift>({
        onDeleteAsync: async (shift) => onDeleteShift(shift.id),
        entityName: 'Shift',
        getDisplayName: (shift) => shift.name,
        getItemId: (shift) => shift.id,
    });

    const locationDeleteHandler = useDeleteHandler<PickupLocation>({
        onDeleteAsync: async (location) => onDeletePickupLocation(location.id),
        entityName: 'Pickup Location',
        getDisplayName: (location) => location.name,
        getItemId: (location) => location.id,
    });

    const sourceDeleteHandler = useDeleteHandler<ReservationSource>({
        onDeleteAsync: async (source) => onDeleteReservationSource(source.id),
        entityName: 'Reservation Source',
        getDisplayName: (source) => source.name,
        getItemId: (source) => source.id,
    });

    const handleSaveDepartment = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!depName) return;

        const departmentData = { name: depName, color: depColor };
        if (editingDep) {
            onSaveDepartment({ ...departmentData, id: editingDep.id });
        } else {
            onSaveDepartment(departmentData);
        }

        setEditingDep(null);
        setDepName('');
        setDepColor('#3b82f6');
    }, [depName, depColor, editingDep, onSaveDepartment]);

    const handleEditDepartment = useCallback((dep: Department) => {
        setEditingDep(dep);
        setDepName(dep.name);
        setDepColor(dep.color);
    }, []);

    const handleSaveShift = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!shiftName) return;
        
        const shiftData = { name: shiftName, startTime: shiftStartTime, endTime: shiftEndTime, color: shiftColor };
        if (editingShift) {
            onSaveShift({ ...shiftData, id: editingShift.id });
        } else {
            onSaveShift(shiftData);
        }

        setEditingShift(null);
        setShiftName('');
        setShiftStartTime('09:00');
        setShiftEndTime('17:00');
        setShiftColor('#34d399');
    }, [shiftName, shiftStartTime, shiftEndTime, shiftColor, editingShift, onSaveShift]);

    const handleEditShift = useCallback((shift: Shift) => {
        setEditingShift(shift);
        setShiftName(shift.name);
        setShiftStartTime(shift.startTime);
        setShiftEndTime(shift.endTime);
        setShiftColor(shift.color);
    }, []);

    const handleCancelEditDepartment = useCallback(() => {
        setEditingDep(null);
        setDepName('');
        setDepColor('#3b82f6');
    }, []);
    
    const handleCancelEditShift = useCallback(() => {
        setEditingShift(null);
        setShiftName('');
        setShiftStartTime('09:00');
        setShiftEndTime('17:00');
        setShiftColor('#34d399');
    }, []);

    const handleSavePickupLocation = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!locationName) return;

        const locationData = { name: locationName, address: locationAddress, latitude: locationLat, longitude: locationLng, color: locationColor };
        if (editingLocation) {
            onSavePickupLocation({ ...locationData, id: editingLocation.id });
        } else {
            onSavePickupLocation(locationData);
        }

        setEditingLocation(null);
        setLocationName('');
        setLocationAddress('');
        setLocationLat(0);
        setLocationLng(0);
        setLocationColor('#f59e0b');
    }, [locationName, locationAddress, locationLat, locationLng, locationColor, editingLocation, onSavePickupLocation]);

    const handleEditPickupLocation = useCallback((location: PickupLocation) => {
        setEditingLocation(location);
        setLocationName(location.name);
        setLocationAddress(location.address);
        setLocationLat(location.latitude);
        setLocationLng(location.longitude);
        setLocationColor(location.color);
    }, []);

    const handleCancelEditPickupLocation = useCallback(() => {
        setEditingLocation(null);
        setLocationName('');
        setLocationAddress('');
        setLocationLat(0);
        setLocationLng(0);
        setLocationColor('#f59e0b');
    }, []);

    const handleSaveReservationSource = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!sourceName) return;

        const sourceData = { name: sourceName };
        if (editingSource) {
            onSaveReservationSource({ ...sourceData, id: editingSource.id });
        } else {
            onSaveReservationSource(sourceData);
        }

        setEditingSource(null);
        setSourceName('');
    }, [sourceName, editingSource, onSaveReservationSource]);

    const handleEditReservationSource = useCallback((source: ReservationSource) => {
        setEditingSource(source);
        setSourceName(source.name);
    }, []);

    const handleCancelEditReservationSource = useCallback(() => {
        setEditingSource(null);
        setSourceName('');
    }, []);

    const handleDepNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setDepName(e.target.value);
    const handleDepColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setDepColor(e.target.value);
    const handleShiftNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftName(e.target.value);
    const handleShiftStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftStartTime(e.target.value);
    const handleShiftEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftEndTime(e.target.value);
    const handleShiftColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftColor(e.target.value);

    const handleLocationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationName(e.target.value);
    const handleLocationAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationAddress(e.target.value);
    const handleLocationLatChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationLat(parseFloat(e.target.value));
    const handleLocationLngChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationLng(parseFloat(e.target.value));
    const handleLocationColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationColor(e.target.value);

    const handleSourceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setSourceName(e.target.value);

    const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
        { id: 'general', label: 'General', icon: <Icons.Settings className="w-5 h-5" /> },
        { id: 'departments', label: 'Departments', icon: <Icons.Users className="w-5 h-5" /> },
        { id: 'shifts', label: 'Shifts', icon: <Icons.Clock className="w-5 h-5" /> },
        { id: 'locations', label: 'Pickup Locations', icon: <Icons.MapPin className="w-5 h-5" /> },
        { id: 'sources', label: 'Reservation Sources', icon: <Icons.List className="w-5 h-5" /> },
        { id: 'admins', label: 'Admins', icon: <Icons.Shield className="w-5 h-5" /> },
    ];

    return (
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:w-64 flex-shrink-0">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Settings</h2>
                    </div>
                    <nav className="p-2 space-y-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                                    activeTab === tab.id
                                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1">
                {activeTab === 'general' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">General Settings</h2>
                            <p className="text-gray-500 dark:text-gray-400">Configure global application preferences.</p>
                        </div>
                        
                        <div className="max-w-md space-y-6">
                            <div>
                                <label htmlFor="propertyName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Property Name
                                </label>
                                <input
                                    type="text"
                                    id="propertyName"
                                    value={propertyName}
                                    onChange={(e) => onUpdatePropertyName(e.target.value)}
                                    placeholder="e.g. Quality INN & SUITES"
                                    className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    This name will be displayed on the login page and in the application header.
                                </p>
                            </div>

                            <div>
                                <label htmlFor="weekStartDay" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Week Start Day
                                </label>
                                <select
                                    id="weekStartDay"
                                    value={weekStartDay}
                                    onChange={(e) => onUpdateWeekStartDay(e.target.value as DayOfWeek)}
                                    className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                >
                                    {ALL_DAYS.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    This determines the first day of the week in the scheduler, calendars, and reporting views.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'departments' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Manage Departments</h2>
                            <p className="text-gray-500 dark:text-gray-400">Create and organize departments for your staff.</p>
                        </div>

                        <form onSubmit={handleSaveDepartment} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingDep ? 'Edit Department' : 'Add New Department'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
                                <div className="space-y-2">
                                    <label htmlFor="depName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department Name</label>
                                    <input type="text" id="depName" value={depName} onChange={handleDepNameChange}
                                        className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="depColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Brand Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="depColor" value={depColor} onChange={handleDepColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingDep ? 'Update' : 'Add'}
                                            </button>
                                            {editingDep && (
                                                <button type="button" onClick={handleCancelEditDepartment} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {departments.map(dep => (
                                <div key={dep.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-inner" style={{ backgroundColor: dep.color }}>
                                            <Icons.Users className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900 dark:text-white">{dep.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest">{dep.color}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditDepartment(dep)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => departmentDeleteHandler.initiateDelete(dep)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'shifts' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Manage Shifts</h2>
                            <p className="text-gray-500 dark:text-gray-400">Define work shifts and their time ranges.</p>
                        </div>

                        <form onSubmit={handleSaveShift} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingShift ? 'Edit Shift' : 'Add New Shift'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="shiftName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shift Name</label>
                                    <input type="text" id="shiftName" value={shiftName} onChange={handleShiftNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="shiftStartTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Time</label>
                                    <input type="time" id="shiftStartTime" value={shiftStartTime} onChange={handleShiftStartTimeChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="shiftEndTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Time</label>
                                    <input type="time" id="shiftEndTime" value={shiftEndTime} onChange={handleShiftEndTimeChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="shiftColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shift Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="shiftColor" value={shiftColor} onChange={handleShiftColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingShift ? 'Update' : 'Add'}
                                            </button>
                                            {editingShift && (
                                                <button type="button" onClick={handleCancelEditShift} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="space-y-3">
                            {shifts.map(shift => (
                                <div key={shift.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className="w-3 h-12 rounded-full" style={{ backgroundColor: shift.color }}></div>
                                        <div>
                                            <p className="font-bold text-gray-900 dark:text-white">{shift.name}</p>
                                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                                <Icons.Clock className="w-3.5 h-3.5" />
                                                <span>{shift.startTime} — {shift.endTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditShift(shift)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => shiftDeleteHandler.initiateDelete(shift)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'locations' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pickup Locations</h2>
                            <p className="text-gray-500 dark:text-gray-400">Manage shuttle pickup and drop-off points.</p>
                        </div>

                        <form onSubmit={handleSavePickupLocation} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingLocation ? 'Edit Location' : 'Add New Location'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Location Name</label>
                                    <input type="text" id="locationName" value={locationName} onChange={handleLocationNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                                    <input type="text" id="locationAddress" value={locationAddress} onChange={handleLocationAddressChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="locationLat" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Latitude</label>
                                    <input type="number" step="any" id="locationLat" value={locationLat} onChange={handleLocationLatChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="locationLng" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Longitude</label>
                                    <input type="number" step="any" id="locationLng" value={locationLng} onChange={handleLocationLngChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Marker Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="locationColor" value={locationColor} onChange={handleLocationColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingLocation ? 'Update' : 'Add'}
                                            </button>
                                            {editingLocation && (
                                                <button type="button" onClick={handleCancelEditPickupLocation} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {pickupLocations.map(location => (
                                <div key={location.id} className="p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm flex-shrink-0" style={{ backgroundColor: location.color }}>
                                            <Icons.MapPin className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">{location.name}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{location.address || 'No address provided'}</p>
                                            <div className="mt-2 flex gap-3 text-[10px] font-mono text-gray-400 uppercase tracking-tighter">
                                                <span>Lat: {location.latitude.toFixed(4)}</span>
                                                <span>Lng: {location.longitude.toFixed(4)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditPickupLocation(location)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => locationDeleteHandler.initiateDelete(location)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'sources' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Reservation Sources</h2>
                            <p className="text-gray-500 dark:text-gray-400">Manage sources for parking reservations.</p>
                        </div>

                        <form onSubmit={handleSaveReservationSource} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingSource ? 'Edit Source' : 'Add New Source'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="sourceName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source Name</label>
                                    <input type="text" id="sourceName" value={sourceName} onChange={handleSourceNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <div className="flex justify-end gap-3">
                                        <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm transition-colors">
                                            {editingSource ? 'Update' : 'Add'}
                                        </button>
                                        {editingSource && (
                                            <button type="button" onClick={handleCancelEditReservationSource} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {reservationSources.map(source => (
                                <div key={source.id} className="p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 shadow-sm flex-shrink-0">
                                            <Icons.List className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">{source.name}</p>
                                        </div>
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditReservationSource(source)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => sourceDeleteHandler.initiateDelete(source)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'admins' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <AdminManagementView 
                            currentUser={currentUser}
                            adminUsers={adminUsers}
                            departments={departments}
                            onCreateAdmin={onCreateAdmin}
                            onDeleteCurrentUser={onDeleteCurrentUser}
                            onUpdateAdminStatus={onUpdateAdminStatus}
                            onDeleteAdmin={onDeleteAdmin}
                            onSaveAdmin={onSaveAdmin}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            {departmentDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Department"
                    message={`Are you sure you want to delete the "${departmentDeleteHandler.itemToDelete.name}" department? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={departmentDeleteHandler.confirmDelete}
                    onCancel={departmentDeleteHandler.cancelDelete}
                    isConfirming={departmentDeleteHandler.isDeleting}
                />
            )}
            {shiftDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Shift"
                    message={`Are you sure you want to delete the "${shiftDeleteHandler.itemToDelete.name}" shift? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={shiftDeleteHandler.confirmDelete}
                    onCancel={shiftDeleteHandler.cancelDelete}
                    isConfirming={shiftDeleteHandler.isDeleting}
                />
            )}
            {locationDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Pickup Location"
                    message={`Are you sure you want to delete the "${locationDeleteHandler.itemToDelete.name}" location? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={locationDeleteHandler.confirmDelete}
                    onCancel={locationDeleteHandler.cancelDelete}
                    isConfirming={locationDeleteHandler.isDeleting}
                />
            )}
            {sourceDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Reservation Source"
                    message={`Are you sure you want to delete the "${sourceDeleteHandler.itemToDelete.name}" source? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={sourceDeleteHandler.confirmDelete}
                    onCancel={sourceDeleteHandler.cancelDelete}
                    isConfirming={sourceDeleteHandler.isDeleting}
                />
            )}
        </div>
    );
};

export default SettingsView;
